import { useRef, useCallback, useEffect } from "react";

export const useSearchDebounce = (searchFunction, delay = 300) => {
  const debounceTimerRef = useRef(null);
  const abortControllerRef = useRef(null);

  const cleanup = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  const cancelPendingRequest = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  const performSearch = useCallback(
    async (searchTerm, onSearchStart, onSearchComplete, onSearchError) => {
      cancelPendingRequest();

      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;

      if (onSearchStart) {
        onSearchStart();
      }

      try {
        await searchFunction(searchTerm, signal);
        if (onSearchComplete) {
          onSearchComplete();
        }
      } catch (error) {
        if (error.name !== "AbortError") {
          console.log("Search error: ", error);
          if (onSearchError) {
            onSearchError(error);
          }
        }
      }
    },
    [searchFunction, cancelPendingRequest]
  );

  const handleSearch = useCallback(
    (searchTerm, callbacks = {}) => {
      const { onStart, onComplete, onError, minChars = 3 } = callbacks;

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      if (searchTerm.length >= minChars) {
        debounceTimerRef.current = setTimeout(() => {
          performSearch(searchTerm, onStart, onComplete, onError);
        }, delay);
      } else {
        cancelPendingRequest();
      }
    },
    [delay, performSearch, cancelPendingRequest]
  );

  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  return {
    handleSearch,
    cancelPendingRequest,
    cleanup,
  };
};

export default useSearchDebounce;
